import { useAuth } from '@/contexts/AuthContext'
import { useState, useRef, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bell,
  Search,
  Menu,
  ChevronDown,
  LogOut,
  User,
  Settings,
  HelpCircle,
} from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { ConnectionStatus } from '@/contexts/WebSocketContext'
import { cn } from '@/lib/utils'

interface HeaderProps {
  sidebarCollapsed: boolean
}

// Page titles based on route
const pageTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/decisions': 'Decisions',
  '/settings': 'Settings',
  '/verticals/ops': 'Enterprise Operations',
  '/verticals/marketing': 'Marketing & Revenue',
  '/verticals/sports': 'Sports Coaching',
}

export function Header({ sidebarCollapsed }: HeaderProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notificationsRef = useRef<HTMLDivElement>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get current page title
  const getPageTitle = () => {
    for (const [path, title] of Object.entries(pageTitles)) {
      if (location.pathname === path || location.pathname.startsWith(path + '/')) {
        return title
      }
    }
    return 'DecisionFlow'
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-surface-100">
      <div className="flex items-center justify-between h-16 px-6 lg:px-8">
        {/* Left side - Page title & breadcrumb */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-surface-100">
            <Menu className="w-5 h-5 text-surface-600" />
          </button>

          <div>
            <h1 className="font-display text-xl font-semibold text-surface-900">
              {getPageTitle()}
            </h1>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          {/* Connection Status */}
          <ConnectionStatus showWhenConnected />

          {/* Search */}
          <Button variant="ghost" size="icon" className="hidden sm:flex">
            <Search className="w-5 h-5" />
          </Button>

          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative"
            >
              <Bell className="w-5 h-5" />
              {/* Notification badge */}
              <span className="absolute top-2 right-2 w-2 h-2 bg-danger-500 rounded-full" />
            </Button>

            <AnimatePresence>
              {notificationsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-soft-lg border border-surface-100 overflow-hidden"
                >
                  <div className="p-4 border-b border-surface-100">
                    <h3 className="font-semibold text-surface-900">Notifications</h3>
                  </div>
                  <div className="p-4 text-center text-sm text-surface-500">
                    <p>No new notifications</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-surface-200 mx-2" />

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={cn(
                'flex items-center gap-2 p-1.5 pr-3 rounded-xl',
                'hover:bg-surface-100 transition-colors',
                userMenuOpen && 'bg-surface-100'
              )}
            >
              <Avatar
                src={user?.avatar_url || undefined}
                name={user?.name}
                size="sm"
                status="online"
              />
              <span className="hidden sm:block text-sm font-medium text-surface-700">
                {user?.name?.split(' ')[0]}
              </span>
              <ChevronDown className={cn(
                'w-4 h-4 text-surface-400 transition-transform',
                userMenuOpen && 'rotate-180'
              )} />
            </button>

            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-soft-lg border border-surface-100 overflow-hidden"
                >
                  {/* User info */}
                  <div className="p-4 border-b border-surface-100">
                    <p className="font-semibold text-surface-900">{user?.name}</p>
                    <p className="text-sm text-surface-500 truncate">{user?.email}</p>
                  </div>

                  {/* Menu items */}
                  <div className="p-2">
                    <Link
                      to="/settings/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-50 hover:text-surface-900 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-50 hover:text-surface-900 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    <a
                      href="https://docs.decisionflow.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-50 hover:text-surface-900 transition-colors"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Help & Docs
                    </a>
                  </div>

                  {/* Logout */}
                  <div className="p-2 border-t border-surface-100">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-danger-600 hover:bg-danger-50 transition-colors w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  )
}
